import { rootApi } from 'src/services/rootApi';
import { HTTPRequestType } from 'src/services/api.types';

/** The System Dashboard AI layer switch (JT1-1820). Mirrors `AiLayerSwitchService` in the backend. */

export interface AiCoverage {
  total: number;
  /** null when the AI search engine could not be reached. */
  indexed: number | null;
}

export interface AiLayerStatus {
  organizationId: string;
  enabled: boolean;
  coverage: { candidates: AiCoverage; contacts: AiCoverage; companies: AiCoverage };
  changes: { enabled: boolean; at: string; by: string | null }[];
}

/** The agency's monthly AI budget and this month's spend (JT1-1822). Mirrors `AiBudgetService.adminView`. */
export type AiBudgetState = 'none' | 'ok' | 'warning' | 'over';

export interface AiBudgetView {
  /** US dollars per month, or null when no budget is set. */
  budget_usd: number | null;
  spent_usd: number;
  percent_used: number | null;
  state: AiBudgetState;
  by_feature: { feature: string; spent_usd: number; calls: number }[];
  /** First day of the month the figures cover, in Australia/Sydney. */
  month_start: string;
  changes: { before: number | null; after: number | null; at: string; by: string | null }[];
}

export const aiLayerApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getAiLayerStatus: builder.query<AiLayerStatus, string>({
      query: (organizationId) => ({ url: `/system/organizations/${organizationId}/ai-layer` }),
      providesTags: (_result, _error, organizationId) => [{ type: 'AiLayer', id: organizationId }],
    }),

    setAiLayer: builder.mutation<AiLayerStatus, { organizationId: string; enabled: boolean }>({
      query: ({ organizationId, enabled }) => ({
        url: `/system/organizations/${organizationId}/ai-layer`,
        method: HTTPRequestType.PUT,
        data: { enabled },
      }),
      invalidatesTags: (_result, _error, { organizationId }) => [{ type: 'AiLayer', id: organizationId }],
    }),

    indexAiLayer: builder.mutation<{ queued: boolean }, string>({
      query: (organizationId) => ({ url: `/system/organizations/${organizationId}/ai-layer/index`, method: HTTPRequestType.POST }),
    }),

    getAiBudget: builder.query<AiBudgetView, string>({
      query: (organizationId) => ({ url: `/system/organizations/${organizationId}/ai-layer/budget` }),
      providesTags: (_result, _error, organizationId) => [{ type: 'AiLayer', id: `${organizationId}-budget` }],
    }),

    setAiBudget: builder.mutation<AiBudgetView, { organizationId: string; monthlyBudgetUsd: number | null }>({
      query: ({ organizationId, monthlyBudgetUsd }) => ({
        url: `/system/organizations/${organizationId}/ai-layer/budget`,
        method: HTTPRequestType.PUT,
        data: { monthly_budget_usd: monthlyBudgetUsd },
      }),
      invalidatesTags: (_result, _error, { organizationId }) => [{ type: 'AiLayer', id: `${organizationId}-budget` }],
    }),
  }),
});

export const {
  useGetAiLayerStatusQuery,
  useSetAiLayerMutation,
  useIndexAiLayerMutation,
  useGetAiBudgetQuery,
  useSetAiBudgetMutation,
} = aiLayerApi;
